import { Component, useContext, type ReactNode } from "react";
import { en } from "../i18n/en.js";
import { interpolate, LanguageContext, type Translate } from "../i18n/LanguageProvider.js";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface BoundaryProps extends Props {
  t: Translate;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryImpl extends Component<BoundaryProps, State> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // The only record of the crash on a self-hosted box, where nobody is
    // watching a dashboard.
    console.error("Unhandled error in the app:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div
          role="alert"
          style={{
            minHeight: "100svh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "var(--space-xl) var(--space-md)",
            background: "var(--bg)",
            color: "var(--text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "var(--surface)",
              borderRadius: "var(--radius-xl)",
              padding: "var(--space-xl) var(--space-lg)",
              boxShadow: "var(--shadow-md)",
              maxWidth: "420px",
              width: "100%",
              border: "1px solid var(--border)",
            }}
          >
            <h2 style={{ margin: "0 0 var(--space-sm)", fontSize: "var(--text-lg)", fontWeight: 700 }}>
              {this.props.t("error.crashTitle")}
            </h2>
            <p style={{ margin: "0 0 var(--space-lg)", color: "var(--text-mid)", fontSize: "var(--text-sm)" }}>
              {this.state.error?.message ?? this.props.t("error.crashBody")}
            </p>
            <button
              // Clearing the flag alone re-renders the children that just
              // threw, so a deterministic crash comes straight back and the
              // button looks dead. A reload starts from a clean slate; players
              // rejoin from sessionStorage and the host refetches its snapshot.
              onClick={() => window.location.reload()}
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "var(--text-sm)",
                fontWeight: 700,
                cursor: "pointer",
                borderRadius: "var(--radius-md)",
                background: "var(--brand)",
                color: "#fff",
                border: "none",
                minHeight: "48px",
              }}
            >
              {this.props.t("common.tryAgain")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Error handling sits at the edge of the tree, so it also has an English
 * fallback for isolated renders that are not wrapped in LanguageProvider. */
export function ErrorBoundary(props: Props) {
  const context = useContext(LanguageContext);
  const t: Translate = context?.t ?? ((key, params) => interpolate(en[key], params));
  return <ErrorBoundaryImpl {...props} t={t} />;
}
